<template>
  <div class="app">
    <header class="tfli-header">
      <div class="header-content">
        <div class="logo-container">
          <div class="logo swoop-logo">
            <a href="https://swoopfunding.com" target="_blank" rel="noopener noreferrer">
              <img src="https://swoopfunding.com/wp-content/themes/swoopMW50/assets/images/swoop-color.svg" alt="Swoop Funding" />
            </a>
          </div>
          <div class="logo-separator">
            <span class="partnership-text">in partnership with</span>
          </div>
          <div class="logo tfli-logo">
            <img src="./tfli.png" alt="TFLI - The Funding and Lending Institute" />
          </div>
        </div>
      </div>
    </header>
    <main>
      <div class="card">
        <div v-if="hasUrlParamsAutofill" class="email-journey-notice">
          <h3>Final step!</h3>
          <p>You've been referred by Commercial Experts to Swoop Funding. Now simply review your details, create a password, and access your tailored funding matches.</p>
        </div>
        <div class="timeline-container">
          <div class="timeline">
            <div 
              v-for="(step, index) in steps" 
              :key="index"
              class="timeline-step"
              :class="{
                'active': currentStep === index,
                'completed': isStepCompleted(index),
                'upcoming': currentStep < index && !hasUrlParamsAutofill,
                'has-error': validationErrors[step.id] && (currentStep >= index || hasUrlParamsAutofill),
                'has-url-params': hasUrlParamsAutofill
              }"
              @click="handleStepClick(index)"
            >
              <div class="timeline-marker"></div>
              <div class="timeline-content">
                <h2>{{ step.title }}</h2>
                <p class="step-description">{{ step.description }}</p>
                
                <div class="input-group">
                  <div v-if="step.type === 'text' && step.field" class="input-wrapper">
                    <div v-if="step.id === 'name'" class="name-inputs">
                      <input 
                        v-model="formData.firstName"
                        type="text"
                        placeholder="First Name"
                        @keyup.enter="nextStep"
                        :disabled="currentStep < index && !hasUrlParamsAutofill"
                        :class="{ 'error': validationErrors[step.id] && !formData.firstName }"
                      >
                      <input 
                        v-model="formData.lastName"
                        type="text"
                        placeholder="Last Name"
                        @keyup.enter="nextStep"
                        :disabled="currentStep < index && !hasUrlParamsAutofill"
                        :class="{ 'error': validationErrors[step.id] && !formData.lastName }"
                      >
                    </div>
                    <input 
                      v-else-if="step.field !== 'password'"
                      v-model="formData[step.field as keyof FormData]"
                      :type="step.inputType || 'text'"
                      :inputmode="step.inputMode"
                      :pattern="step.pattern"
                      :placeholder="step.placeholder"
                      @keyup.enter="nextStep"
                      @input="handleNumericInput(step.field, $event)"
                      :disabled="currentStep < index && !hasUrlParamsAutofill"
                      :class="{ 'error': validationErrors[step.id] && !formData[step.field as keyof FormData] }"
                    >
                    <div v-else class="password-input-wrapper">
                      <input 
                        v-model="formData.password"
                        :type="showPassword ? 'text' : 'password'"
                        placeholder="Password"
                        @keyup.enter="nextStep"
                        :disabled="currentStep < index && !hasUrlParamsAutofill"
                        :class="{ 'error': validationErrors[step.id] && !formData.password }"
                      >
                      <button 
                        class="toggle-password"
                        @click.stop="showPassword = !showPassword"
                        type="button"
                        :disabled="currentStep < index && !hasUrlParamsAutofill"
                      >
                        {{ showPassword ? 'Hide' : 'Show' }}
                      </button>
                    </div>
                  </div>
                  <div v-if="step.type === 'company'" class="company-inputs">
                    <div class="business-type-toggle">
                      <button 
                        :class="{ active: formData.businessType === 'limited' }"
                        @click.stop="formData.businessType = 'limited'"
                      >
                        Limited Company
                      </button>
                      <button 
                        :class="{ active: formData.businessType === 'sole' }"
                        @click.stop="formData.businessType = 'sole'"
                      >
                        Sole Trader
                      </button>
                    </div>
                    
                    <div v-if="formData.businessType === 'limited'" class="company-search">
                      <input 
                        v-model="formData.companyName"
                        type="text"
                        placeholder="Search for your company"
                        @input="(e: Event) => searchCompanies((e.target as HTMLInputElement).value)"
                        @keyup.enter.prevent="formData.companySearchResults?.length ? null : nextStep"
                        :disabled="currentStep !== index && !hasUrlParamsAutofill"
                        :class="{ 'error': validationErrors[step.id] && !formData.companyName }"
                      >
                      <div v-if="formData.isSearching" class="search-loading">
                        Searching...
                      </div>
                      <div v-if="formData.searchError" class="search-error">
                        {{ formData.searchError }}
                      </div>
                      <div v-if="formData.companySearchResults?.length" class="search-results">
                        <div 
                          v-for="company in formData.companySearchResults" 
                          :key="company.company_number"
                          class="search-result-item"
                          @click.stop="selectCompany(company)"
                        >
                          <div class="company-name">{{ company.title || company.company_name }}</div>
                          <div class="company-details">
                            <div class="company-address">
                              {{ company.address?.premises }}
                              {{ company.address?.address_line_1 }}
                              {{ company.address?.locality ? `, ${company.address.locality}` : '' }}
                              {{ company.address?.region ? `, ${company.address.region}` : '' }}
                              {{ company.address?.postal_code ? `, ${company.address.postal_code}` : '' }}
                            </div>
                            <div class="company-number">Company number: {{ company.company_number }}</div>
                            <div v-if="company.company_status" class="company-status" :class="company.company_status.toLowerCase()">
                              {{ company.company_status }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <input 
                      v-model="formData.companyName"
                      type="text"
                      :placeholder="formData.businessType === 'limited' ? 'Company Name' : 'Business Name'"
                      @keyup.enter="nextStep"
                      :disabled="currentStep !== index && !hasUrlParamsAutofill"
                      :class="{ 'error': validationErrors[step.id] && !formData.companyName }"
                    >
                    
                    <input 
                      v-if="formData.businessType === 'limited'"
                      v-model="formData.registrationNumber"
                      type="text"
                      placeholder="Registration Number (8 digits)"
                      @keyup.enter="nextStep"
                      :disabled="currentStep !== index && !hasUrlParamsAutofill"
                      :class="{ 'error': validationErrors[step.id] && !formData.registrationNumber }"
                    >
                    
                    <input 
                      v-model="formData.postcode"
                      type="text"
                      placeholder="Postcode"
                      @keyup.enter="nextStep"
                      :disabled="currentStep !== index && !hasUrlParamsAutofill"
                      :class="{ 'error': validationErrors[step.id] && !formData.postcode }"
                    >
                  </div>
                </div>

                <div class="button-group" v-if="currentStep === index">
                  <button 
                    v-if="index > 0" 
                    @click.stop="previousStep"
                    class="secondary"
                  >
                    Back
                  </button>
                  <button 
                    @click.stop="nextStep"
                    :disabled="!isStepValid || (index === steps.length - 1 && isSubmitting)"
                  >
                    <span v-if="index === steps.length - 1 && isSubmitting" class="button-loading">
                      <span class="spinner"></span>
                      <span>Processing...</span>
                    </span>
                    <span v-else>{{ index === steps.length - 1 ? 'Access my funding matches' : 'Next' }}</span>
                  </button>
                </div>
                <div v-if="error && currentStep === index" class="error-message" v-html="error">
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
    <div class="trust-indicator">
      <p>Trusted by over 250,000 businesses globally</p>
    </div>
    <!-- #notadev😝 - Toast notification for company verification status -->
    <div v-if="verificationNotice" class="toast-notification" :class="verificationNotice.type">
      <span class="toast-icon">{{ verificationNotice.icon }}</span>
      <span class="toast-message">{{ verificationNotice.message }}</span>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, nextTick, onMounted } from 'vue'
import axios from 'axios'

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  contactNumber: string;
  companyName: string;
  registrationNumber: string;
  country: string;
  postcode: string;
  turnoverLastYear: string;
  fundingAmount: string;
  companySearchResults: CompanyItem[];
  isSearching: boolean;
  searchError: string;
  sourceUrl: string;
  businessType: 'limited' | 'sole';
}

interface CompanyItem {
  company_name: string;
  company_number: string;
  company_status: string;
  date_of_creation: string;
  address_snippet?: string;
  title?: string;
  address?: {
    premises?: string;
    address_line_1?: string;
    address_line_2?: string;
    locality?: string;
    region?: string;
    postal_code?: string;
    country?: string;
  };
}

interface Step {
  id: string;
  title: string;
  description: string;
  type: 'text' | 'company';
  field?: keyof FormData;
  inputType?: string;
  inputMode?: string;
  pattern?: string;
  placeholder?: string;
  validate: (value: FormData) => boolean;
  isCompleted: boolean;
  isActive: boolean;
  isSearching?: boolean;
  searchError?: string;
  sourceUrl?: string;
}

export default defineComponent({
  name: 'App',
  setup() {
    const currentStep = ref(0)
    const isSubmitting = ref(false)
    const error = ref('')
    const currentInput = ref<HTMLInputElement | null>(null)
    const companyNameInput = ref<HTMLInputElement | null>(null)
    const showPassword = ref(false)
    const validationErrors = ref<Record<string, boolean>>({})
    const completedSteps = ref<Set<number>>(new Set())
    const hasUrlParamsAutofill = ref(false)
    // #notadev😝 - Verification notice for company lookup status
    const verificationNotice = ref<{type: string, icon: string, message: string} | null>(null)

    const formData = ref<FormData>({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      contactNumber: '',
      companyName: '',
      registrationNumber: '',
      country: 'United Kingdom',
      postcode: '',
      turnoverLastYear: '',
      fundingAmount: '',
      companySearchResults: [],
      isSearching: false,
      searchError: '',
      sourceUrl: window.location.href,
      businessType: 'limited'
    })

    const steps = ref<Step[]>([
      {
        id: 'funding',
        title: 'How much funding do you need?',
        description: 'Enter the amount you\'re looking to raise for your business',
        type: 'text',
        field: 'fundingAmount',
        inputType: 'text',
        inputMode: 'numeric',
        pattern: '[0-9,]*',
        placeholder: 'Enter amount in £',
        validate: (value: FormData): boolean => {
          const numValue = Number(value.fundingAmount.replace(/,/g, ''));
          return !isNaN(numValue) && numValue >= 0;
        },
        isCompleted: false,
        isActive: true,
        isSearching: false,
        searchError: '',
        sourceUrl: window.location.href
      },
      {
        id: 'company',
        title: 'Tell us about your business',
        description: 'We\'ll help you find your company details or you can enter them manually',
        type: 'company',
        validate: (value: FormData): boolean => {
          if (value.businessType === 'limited') {
            return value.companyName.trim().length > 0 && 
              /^\d{8}$/.test(value.registrationNumber.trim()) &&
              /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i.test(value.postcode.trim());
          } else {
            return value.companyName.trim().length > 0 &&
              /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i.test(value.postcode.trim());
          }
        },
        isCompleted: false,
        isActive: false,
        isSearching: false,
        searchError: '',
        sourceUrl: window.location.href
      },
      {
        id: 'turnover',
        title: 'What\'s your estimated annual revenue?',
        description: 'Enter your business\'s estimated annual revenue',
        type: 'text',
        field: 'turnoverLastYear',
        inputType: 'text',
        inputMode: 'numeric',
        pattern: '[0-9,]*',
        placeholder: 'Enter amount in £',
        validate: (value: FormData): boolean => {
          const numValue = Number(value.turnoverLastYear.replace(/,/g, ''));
          return !isNaN(numValue) && numValue >= 0;
        },
        isCompleted: false,
        isActive: false,
        isSearching: false,
        searchError: '',
        sourceUrl: window.location.href
      },
      {
        id: 'email',
        title: 'Your email address',
        description: 'We\'ll use this to keep you updated on your application',
        type: 'text',
        field: 'email',
        inputType: 'email',
        placeholder: 'Enter your email address',
        validate: (value: FormData): boolean => {
          return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.email.trim());
        },
        isCompleted: false,
        isActive: false,
        isSearching: false,
        searchError: '',
        sourceUrl: window.location.href
      },
      {
        id: 'name',
        title: 'Your name',
        description: 'Let us know who we\'re speaking to',
        type: 'text',
        field: 'firstName',
        inputType: 'text',
        placeholder: 'Enter your name',
        validate: (value: FormData): boolean => {
          return Boolean(value.firstName?.trim() && value.lastName?.trim());
        },
        isCompleted: false,
        isActive: false,
        isSearching: false,
        searchError: '',
        sourceUrl: window.location.href
      },
      {
        id: 'phone',
        title: 'Your phone number',
        description: 'We\'ll use this to contact you about your application',
        type: 'text',
        field: 'contactNumber',
        inputType: 'tel',
        placeholder: 'Enter your phone number',
        validate: (value: FormData): boolean => {
          return /^\+?[\d\s-]{10,}$/.test(value.contactNumber.trim());
        },
        isCompleted: false,
        isActive: false,
        isSearching: false,
        searchError: '',
        sourceUrl: window.location.href
      },
      {
        id: 'password',
        title: 'Create your password',
        description: 'Everything look correct? Just set your password to access your Swoop dashboard and see your funding matches.',
        type: 'text',
        field: 'password',
        inputType: 'password',
        placeholder: 'Create a password (min 8 characters)',
        validate: (value: FormData): boolean => {
          return value.password.length >= 8;
        },
        isCompleted: false,
        isActive: false,
        isSearching: false,
        searchError: '',
        sourceUrl: window.location.href
      }
    ])

    const isStepValid = computed(() => {
      const step = steps.value[currentStep.value];
      if (!step) return false;
      return step.validate(formData.value);
    })

    const isStepCompleted = (index: number) => {
      return completedSteps.value.has(index)
    }

    const markStepAsCompleted = (index: number) => {
      completedSteps.value.add(index)
    }

    const focusInput = async () => {
      await nextTick()
      const step = steps.value[currentStep.value]
      if (!step) return

      if (step.type === 'company') {
        const input = document.querySelector(`.timeline-step:nth-child(${currentStep.value + 1}) .company-search input`) as HTMLInputElement
        if (input) {
          input.focus()
        }
      } else if (step.type === 'text') {
        const input = document.querySelector(`.timeline-step:nth-child(${currentStep.value + 1}) .input-wrapper input`) as HTMLInputElement
        if (input) {
          input.focus()
        }
      }
    }

    const validateAllSteps = () => {
      const errors: Record<string, boolean> = {}
      
      steps.value.forEach(step => {
        if (step.type === 'text') {
          if (step.id === 'name') {
            if (!formData.value.firstName || !formData.value.lastName) {
              errors[step.id] = true
            }
          } else if (step.field) {
            if (!formData.value[step.field as keyof FormData]) {
              errors[step.id] = true
            }
          }
        } else if (step.type === 'company') {
          if (!formData.value.companyName || !formData.value.postcode) {
            errors[step.id] = true
          }
          if (formData.value.businessType === 'limited' && !formData.value.registrationNumber) {
            errors[step.id] = true
          }
        }
      })
      
      validationErrors.value = errors
      return Object.keys(errors).length === 0
    }

    const nextStep = async () => {
      if (currentStep.value < steps.value.length - 1) {
        if (steps.value[currentStep.value].validate(formData.value)) {
          markStepAsCompleted(currentStep.value)
          error.value = '' // Clear any errors when moving to next step
          currentStep.value++
          await nextTick()
          setTimeout(focusInput, 200)
        }
      } else {
        if (validateAllSteps()) {
          submitForm()
        } else {
          // Find the first step with an error and navigate to it
          const firstErrorStep = steps.value.findIndex(step => validationErrors.value[step.id])
          if (firstErrorStep !== -1) {
            currentStep.value = firstErrorStep
            await nextTick()
            setTimeout(focusInput, 200)
          }
        }
      }
    }

    const previousStep = async () => {
      if (currentStep.value > 0) {
        currentStep.value--
        await nextTick()
        setTimeout(focusInput, 200)
      }
    }

    const submitForm = async () => {
      isSubmitting.value = true;
      error.value = '';
      
      // Get API URL from environment variable, with localhost fallback for development
      const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://localhost:7071/api';
      console.log('Using API URL:', apiBaseUrl);
      
      try {
        const response = await axios.post(`${apiBaseUrl}/register`, formData.value);
        
        // Check if we have a successful response
        if (response.data.success) {
          // If we have an auth token, append it to the redirect URL
          if (response.data.authToken) {
            // The Swoop app needs the token in a specific format
            // We'll pass it as a base64 encoded query parameter
            const authData = {
              token: response.data.authToken,
              email: formData.value.email,
              userId: response.data.userId,
              companyId: response.data.companyId
            };
            const encodedAuth = btoa(JSON.stringify(authData));
            window.location.href = `${response.data.redirectUrl}?auth=${encodedAuth}`;
          } else {
            // Simple redirect - user will need to log in manually
            alert('Registration successful! You will be redirected to the Swoop app to log in.');
            setTimeout(() => {
              window.location.href = response.data.redirectUrl || 'https://app.stage.swoopfunding.com/matches/';
            }, 2000);
          }
        } else {
          throw new Error('Registration failed');
        }
      } catch (err: any) {
        // Check if the error is about email already existing
        const errorMessage = Array.isArray(err.response?.data?.error) 
          ? err.response.data.error.join(' ') 
          : err.response?.data?.error || '';
          
        if (errorMessage.toLowerCase().includes('email already exists')) {
          error.value = 'This email is already registered. Please <a href="https://app.swoopfunding.com/login/" target="_blank" style="color: #0066cc; text-decoration: underline;">click here to log in</a>.';
        } else {
          error.value = err.response?.data?.details?.join('\n') || 
                       (Array.isArray(err.response?.data?.error) ? err.response.data.error.join('\n') : errorMessage) || 
                       'An error occurred during registration';
        }
      } finally {
        isSubmitting.value = false;
      }
    };

    const API_BASE = 'https://stage-ch-proxy.azurewebsites.net/api/companies';

    let searchTimeout: any = null;
    
    const searchCompanies = async (companyName: string) => {
      if (formData.value.businessType !== 'limited') return;
      if (!companyName || companyName.length < 2) {
        formData.value.companySearchResults = [];
        return;
      }
      
      // Clear previous timeout
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      
      // Debounce the search
      searchTimeout = setTimeout(async () => {
        formData.value.isSearching = true;
        formData.value.searchError = '';
        
        try {
          const response = await fetch(
            `${API_BASE}/search?q=${encodeURIComponent(companyName)}`
          );

          if (!response.ok) {
            throw new Error(`Companies House API error: ${response.status}`);
          }

          const data = await response.json();
          formData.value.companySearchResults = data.items || [];
        } catch (error) {
          console.error('Error searching companies:', error);
          formData.value.searchError = 'Failed to search companies. Please try again.';
        } finally {
          formData.value.isSearching = false;
        }
      }, 300); // 300ms debounce
    };

    // #notadev😝 - Function to lookup company by registration number
    const lookupCompanyByNumber = async (registrationNumber: string): Promise<any> => {
      if (!registrationNumber || !/^\d{8}$/.test(registrationNumber)) {
        throw new Error('Invalid registration number format');
      }

      const response = await fetch(
        `${API_BASE}/details?number=${encodeURIComponent(registrationNumber)}`
      );

      if (!response.ok) {
        throw new Error(`Company not found: ${response.status}`);
      }

      const data = await response.json();
      
      // #notadev😝 - Check if the response indicates an error (some APIs return 200 with error in body)
      if (data.error || (!data.company_name && !data.title && !data.company_number)) {
        throw new Error('Company not found in Companies House');
      }

      return data;
    };

    const selectCompany = async (company: CompanyItem) => {
      try {
        const response = await fetch(
          `${API_BASE}/details?number=${encodeURIComponent(company.company_number)}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch company details');
        }

        const companyData = await response.json();
        
        // Map the company data to form fields
        formData.value.companyName = companyData.title || companyData.company_name || '';
        formData.value.registrationNumber = companyData.company_number || '';
        
        const address = companyData.registered_office_address || companyData.address || {};
        formData.value.postcode = (address.postal_code || '').toUpperCase().replace(/\s/g, '');
        formData.value.country = address.country || 'England';
        
        // Clear search results after selection
        formData.value.companySearchResults = [];
        formData.value.isSearching = false;
        formData.value.searchError = '';
      } catch (error) {
        console.error('Error getting company details:', error);
        formData.value.searchError = 'Failed to get company details. Please try again.';
      }
    };

    const handleStepClick = (index: number) => {
      // Allow clicking on any step if URL params were used for autofill
      // Otherwise only allow clicking on steps up to current step
      if (hasUrlParamsAutofill.value || index <= currentStep.value) {
        // Clear any existing errors when changing steps
        error.value = ''
        currentStep.value = index
        nextTick(() => {
          const element = document.querySelector(`.timeline-step:nth-child(${index + 1})`)
          if (element) {
            const rect = element.getBoundingClientRect()
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop
            const targetPosition = rect.top + scrollTop - (window.innerHeight * 0.3)
            
            window.scrollTo({
              top: targetPosition,
              behavior: 'smooth'
            })
            
            setTimeout(focusInput, 500)
          }
        })
      }
    }

    const handleNumericInput = (field: string | undefined, event: Event) => {
      if (!field || (field !== 'fundingAmount' && field !== 'turnoverLastYear')) return
      
      const target = event.target as HTMLInputElement
      let value = target.value.replace(/[^\d]/g, '') // Remove non-digits
      
      if (value) {
        // Add commas for thousands
        value = parseInt(value).toLocaleString()
      }
      
      // Update the form data
      (formData.value as any)[field] = value
    }

    onMounted(async () => {
      // Set page title and favicon
      document.title = 'Funding Application | Swoop'
      
      // Set favicon
      const favicon = document.querySelector('link[rel="icon"]') || document.createElement('link')
      favicon.setAttribute('rel', 'icon')
      favicon.setAttribute('type', 'image/png')
      favicon.setAttribute('href', 'https://swoopfunding.com/ca/wp-content/uploads/sites/2/2021/06/favicon-80x80.png')
      if (!document.querySelector('link[rel="icon"]')) {
        document.head.appendChild(favicon)
      }
      
      const urlParams = new URLSearchParams(window.location.search)
      const fields: (keyof FormData)[] = [
        'firstName', 'lastName', 'email', 'contactNumber',
        'companyName', 'registrationNumber', 'postcode', 'turnoverLastYear', 'fundingAmount'
      ]
      
      let hasUrlParams = false
      fields.forEach(field => {
        const value = urlParams.get(field)
        if (value) {
          // Special handling for numeric fields
          if (field === 'turnoverLastYear' || field === 'fundingAmount') {
            // Parse the number and format with commas
            const numericValue = decodeURIComponent(value).replace(/[^\d]/g, '')
            let formattedValue = ''
            if (numericValue) {
              formattedValue = parseInt(numericValue, 10).toLocaleString()
            }
            (formData.value as any)[field] = formattedValue
          } else {
            // Normal string values
            (formData.value as any)[field] = decodeURIComponent(value)
          }
          
          hasUrlParams = true
        }
      })

      // Set the flag for URL parameter autofill
      hasUrlParamsAutofill.value = hasUrlParams

      if (hasUrlParams) {
        // #notadev😝 - Auto-lookup company if registration number is provided
        console.log('Has URL params:', hasUrlParams);
        console.log('Registration number:', formData.value.registrationNumber);
        console.log('Registration number valid?', /^\d{8}$/.test(formData.value.registrationNumber));
        
        // #notadev😝 - Handle invalid registration number format
        if (formData.value.registrationNumber && !(/^\d{8}$/.test(formData.value.registrationNumber))) {
          console.log('Invalid registration number format');
          // Still set to limited company if they provided a registration number
          formData.value.businessType = 'limited';
          
          // Show warning notification
          verificationNotice.value = {
            type: 'info',
            icon: '⚠️',
            message: 'Could not verify company on Companies House. Please check the registration number.'
          };
          
          setTimeout(() => {
            verificationNotice.value = null;
          }, 5000);
        }
        else if (formData.value.registrationNumber && /^\d{8}$/.test(formData.value.registrationNumber)) {
          console.log('Starting company lookup...');
          // Set business type to limited if we have a registration number
          formData.value.businessType = 'limited';
          
          // Show loading state
          formData.value.isSearching = true;
          
          try {
            const companyData = await lookupCompanyByNumber(formData.value.registrationNumber);
            
            // Override with official company data
            formData.value.companyName = companyData.title || companyData.company_name || formData.value.companyName;
            
            const address = companyData.registered_office_address || companyData.address || {};
            if (address.postal_code) {
              formData.value.postcode = address.postal_code.toUpperCase().replace(/\s/g, '');
            }
            formData.value.country = address.country || 'United Kingdom';
            
            // Show success notification
            verificationNotice.value = {
              type: 'success',
              icon: '✓',
              message: 'Company details verified with Companies House'
            };
            
            // Clear notification after 5 seconds
            setTimeout(() => {
              verificationNotice.value = null;
            }, 5000);
            
          } catch (error) {
            // #notadev😝 - Enhanced error logging
            console.error('Company lookup failed:', error);
            console.log('Setting verification notice for error case');
            
            // Show info notification
            verificationNotice.value = {
              type: 'info',
              icon: 'ℹ️',
              message: 'Could not verify company on Companies House. Please check the registration number.'
            };
            
            console.log('Verification notice set:', verificationNotice.value);
            
            // Clear notification after 5 seconds
            setTimeout(() => {
              verificationNotice.value = null;
            }, 5000);
          } finally {
            formData.value.isSearching = false;
          }
        }
        // Mark steps as completed based on filled data
        steps.value.forEach((step, index) => {
          if (step.type === 'text') {
            if (step.id === 'name' && formData.value.firstName && formData.value.lastName) {
              markStepAsCompleted(index)
            } else if (step.field) {
              const fieldValue = formData.value[step.field as keyof FormData]
              if (fieldValue) {
                markStepAsCompleted(index)
              }
            }
          } else if (step.type === 'company') {
            if (formData.value.companyName && formData.value.postcode) {
              if (formData.value.businessType === 'limited') {
                if (formData.value.registrationNumber) {
                  markStepAsCompleted(index)
                }
              } else {
                markStepAsCompleted(index)
              }
            }
          }
        })

        const passwordStepIndex = steps.value.findIndex(step => step.id === 'password')
        if (passwordStepIndex !== -1) {
          currentStep.value = passwordStepIndex
        }
      }
      
      // Initial focus
      focusInput()
    })

    // Watch for step changes to handle focus
    watch(currentStep, async () => {
      await nextTick()
      setTimeout(focusInput, 200)
    })

    return {
      currentStep,
      steps,
      formData,
      isStepValid,
      isSubmitting,
      error,
      nextStep,
      previousStep,
      submitForm,
      currentInput,
      companyNameInput,
      showPassword,
      focusInput,
      searchCompanies,
      selectCompany,
      handleStepClick,
      handleNumericInput,
      validationErrors,
      isStepCompleted,
      hasUrlParamsAutofill,
      verificationNotice
    }
  }
})
</script>

<style>
/* Typography System */
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

/* Global Reset and Base Styles */
html, body {
  margin: 0;
  padding: 0;
  background-color: #f9fafb;
  min-height: 100vh;
}

/* CSS Variables for Modern Theming */
:root {
  /* Primary Colors */
  --primary: #46c8bb;
  --primary-dark: #3ab5a8;
  --primary-light: #5ed4c8;
  --primary-bg: rgba(70, 200, 187, 0.1);
  
  /* Neutral Colors */
  --text-primary: #1a1a1a;
  --text-secondary: #6b7280;
  --text-tertiary: #9ca3af;
  --text-on-primary: #ffffff;
  
  /* Background Colors */
  --bg-primary: #ffffff;
  --bg-secondary: #f9fafb;
  --bg-tertiary: #f3f4f6;
  --bg-hover: #f7f8fa;
  
  /* Border Colors */
  --border-light: #e5e7eb;
  --border-medium: #d1d5db;
  --border-focus: var(--primary);
  
  /* Shadow System */
  --shadow-xs: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  --shadow-sm: 0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 1px 2px -1px rgba(0, 0, 0, 0.03);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.07), 0 2px 4px -2px rgba(0, 0, 0, 0.03);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -4px rgba(0, 0, 0, 0.03);
  --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.03);
  
  /* Status Colors */
  --success: #10b981;
  --warning: #f59e0b;
  --error: #ef4444;
  --info: #3b82f6;
  
  /* Spacing Scale */
  --space-xs: 0.25rem;
  --space-sm: 0.5rem;
  --space-md: 1rem;
  --space-lg: 1.5rem;
  --space-xl: 2rem;
  
  /* Border Radius */
  --radius-sm: 0.375rem;
  --radius-md: 0.5rem;
  --radius-lg: 0.75rem;
  --radius-xl: 1rem;
  --radius-full: 9999px;
  
  /* Transitions */
  --transition-fast: 150ms cubic-bezier(0.4, 0, 0.2, 1);
  --transition-base: 200ms cubic-bezier(0.4, 0, 0.2, 1);
  --transition-slow: 300ms cubic-bezier(0.4, 0, 0.2, 1);
  
  /* Typography Scale */
  --font-size-xs: 0.75rem;
  --font-size-sm: 0.875rem;
  --font-size-base: 1rem;
  --font-size-lg: 1.125rem;
  --font-size-xl: 1.25rem;
  --font-size-2xl: 1.5rem;
  
  /* Font Weights */
  --font-normal: 400;
  --font-medium: 500;
  --font-semibold: 600;
  --font-bold: 700;
  
  /* Line Heights */
  --leading-tight: 1.25;
  --leading-normal: 1.5;
  --leading-relaxed: 1.75;
  
  /* Letter Spacing */
  --tracking-tighter: -0.05em;
  --tracking-tight: -0.025em;
  --tracking-normal: 0;
  --tracking-wide: 0.025em;
}

/* Ensure light theme consistency */
* {
  box-sizing: border-box;
}

/* Global body reset */
body {
  margin: 0;
  padding: 0;
  background-color: var(--bg-secondary);
}

/* Base container styles */
.app {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  font-size: var(--font-size-base);
  font-weight: var(--font-normal);
  line-height: var(--leading-normal);
  color: var(--text-primary);
  letter-spacing: var(--tracking-normal);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  
  max-width: 1000px;
  margin: 0 auto;
  padding: var(--space-md);
  box-sizing: border-box;
  width: 100%;
  overflow-x: hidden;
  min-height: 100vh;
}

/* Card container */
.card {
  background: var(--bg-primary);
  border-radius: var(--radius-lg);
  padding: var(--space-lg);
  margin: 0 auto;
  box-sizing: border-box;
  width: 100%;
  max-width: 800px;
  box-shadow: var(--shadow-lg);
}

/* Email journey notice */
.email-journey-notice {
  background: var(--bg-primary);
  border-radius: var(--radius-lg);
  padding: var(--space-lg);
  margin-bottom: var(--space-xl);
  text-align: center;
  box-shadow: var(--shadow-md);
}

.email-journey-notice h3 {
  font-size: var(--font-size-xl);
  font-weight: var(--font-bold);
  color: var(--text-primary);
  margin: 0 0 var(--space-md) 0;
}

.email-journey-notice p {
  font-size: var(--font-size-lg);
  font-weight: var(--font-medium);
  color: var(--text-secondary);
  margin: 0;
  line-height: var(--leading-relaxed);
}

/* #notadev😝 - Toast notification styles */
.toast-notification {
  position: fixed;
  bottom: var(--space-md);
  left: 50%;
  transform: translateX(-50%);
  background: var(--bg-primary);
  border-radius: var(--radius-md);
  padding: var(--space-sm) var(--space-md);
  box-shadow: var(--shadow-lg);
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  font-size: var(--font-size-sm);
  font-weight: var(--font-medium);
  animation: slideUp 0.3s ease-out;
  border: 2px solid transparent;
  transition: all var(--transition-base);
  z-index: 1000;
  max-width: 420px;
  min-width: auto;
}

.toast-notification.success {
  background: var(--success);
  color: white;
  border-color: var(--success);
}

.toast-notification.info {
  background: var(--info);
  color: white;
  border-color: var(--info);
}

.toast-icon {
  font-size: var(--font-size-lg);
  display: inline-flex;
  align-items: center;
}

.toast-message {
  flex: 1;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(100%);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}


/* Timeline container */
.timeline-container {
  padding: var(--space-sm);
}

/* Timeline */
.timeline {
  position: relative;
  max-width: 650px;
  margin: 0 auto;
}

/* Timeline line */
.timeline::before {
  content: '';
  position: absolute;
  left: 12px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: var(--border-light);
  z-index: 0;
}

/* Timeline marker */
.timeline-marker {
  position: absolute;
  left: 5px;
  top: 0.7rem;
  width: 16px;
  height: 16px;
  border-radius: var(--radius-full);
  background: var(--primary);
  border: 2px solid var(--bg-primary);
  z-index: 2;
  transition: background-color var(--transition-slow), box-shadow var(--transition-slow);
  box-shadow: 0 0 0 2px var(--primary-bg);
  transform: translateX(0) translateY(0);
}

/* Timeline step */
.timeline-step {
  position: relative;
  padding-left: 32px;
  margin-bottom: var(--space-md);
  transition: all var(--transition-slow);
  cursor: pointer;
}

/* Timeline content */
.timeline-content {
  background: var(--bg-primary);
  padding: var(--space-md);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  transition: all var(--transition-slow);
  border: 1px solid var(--border-light);
}

/* Step title and description */
.timeline-content h2 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-semibold);
  margin-bottom: var(--space-xs);
  color: var(--text-primary);
  transition: all var(--transition-base);
  letter-spacing: var(--tracking-tight);
  line-height: var(--leading-tight);
}

.step-description {
  color: var(--text-secondary);
  margin-bottom: var(--space-sm);
  font-size: var(--font-size-sm);
  line-height: var(--leading-relaxed);
  transition: all var(--transition-base);
  letter-spacing: var(--tracking-normal);
}

/* Active step styles */
.timeline-step.active .timeline-marker {
  background: var(--primary);
  box-shadow: 0 0 0 4px var(--primary-bg);
  animation: pulse 1.5s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  left: 5px;
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 0 0 4px var(--primary-bg);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(70, 200, 187, 0.1);
  }
}

.timeline-step.active .timeline-content {
  box-shadow: var(--shadow-md);
  transform: translateX(4px);
  border-color: var(--primary);
  background: var(--bg-primary);
  border-width: 2px;
}

.timeline-step.active h2 {
  color: var(--text-primary);
  font-weight: var(--font-semibold);
}

.timeline-step.active .step-description {
  color: var(--text-secondary);
}

/* Upcoming step styles */
.timeline-step.upcoming .timeline-content {
  opacity: 0.6;
  transform: translateX(0) scale(0.98);
  background: var(--bg-secondary);
  filter: grayscale(0.1);
}

.timeline-step.upcoming .timeline-marker {
  background: var(--border-medium);
  box-shadow: none;
  left: 5px;
}

.timeline-step.upcoming h2,
.timeline-step.upcoming .step-description {
  color: var(--text-tertiary);
  font-weight: var(--font-normal);
}

/* Completed step styles */
.timeline-step.completed .timeline-marker {
  background: var(--success);
  left: 5px;
  transform: translateX(0) translateY(0);
}

.timeline-step.completed .timeline-marker::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 6px;
  height: 6px;
  background: var(--bg-primary);
  border-radius: var(--radius-full);
}

.timeline-step.completed .timeline-content {
  opacity: 0.9;
  background: var(--bg-primary);
  transition: all var(--transition-base);
}

.timeline-step.completed:hover .timeline-content {
  opacity: 1;
  transform: translateX(2px);
  box-shadow: var(--shadow-sm);
}

/* Input group */
.input-group {
  margin: var(--space-sm) 0;
  transition: all var(--transition-base);
}

/* Button group */
.button-group {
  margin-top: var(--space-md);
  display: flex;
  gap: var(--space-sm);
  justify-content: flex-end;
  transition: all var(--transition-base);
}

/* Input styles */
input {
  height: 44px;
  font-size: var(--font-size-base);
  padding: 0 var(--space-md);
  margin-bottom: var(--space-sm);
  transition: all var(--transition-base);
  border: 2px solid var(--border-light);
  border-radius: var(--radius-md);
  letter-spacing: var(--tracking-normal);
  width: 100%;
  box-sizing: border-box;
  font-family: inherit;
  color: var(--text-primary);
  background: var(--bg-primary);
  font-weight: var(--font-medium);
  box-shadow: var(--shadow-xs);
}

input::placeholder {
  font-size: var(--font-size-sm);
  color: var(--text-tertiary);
  font-weight: var(--font-normal);
  letter-spacing: var(--tracking-normal);
}

input:focus {
  border-color: var(--primary);
  background: var(--bg-primary);
  box-shadow: 0 0 0 3px var(--primary-bg), var(--shadow-md);
  outline: none;
  transform: translateY(-1px);
}

button {
  padding: var(--space-sm) var(--space-lg);
  font-size: var(--font-size-base);
  min-width: 100px;
  transition: all var(--transition-base);
  border-radius: var(--radius-lg);
  font-weight: var(--font-semibold);
  letter-spacing: var(--tracking-tight);
  border: none;
  font-family: inherit;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  isolation: isolate;
  box-shadow: var(--shadow-sm);
}

button:not(:disabled) {
  background: var(--primary);
  color: var(--text-on-primary);
  transition: all var(--transition-base);
  transform: translateY(0);
}

/* Button ripple effect */
button:not(:disabled)::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, transparent 70%);
  opacity: 0;
  transform: scale(0);
  transition: opacity 0.5s, transform 0.5s;
}

button:not(:disabled):active::before {
  opacity: 1;
  transform: scale(1);
  transition: 0s;
}

button:not(:disabled):hover {
  background: var(--primary-dark);
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}

button:not(:disabled):active {
  transform: translateY(0);
  box-shadow: var(--shadow-sm);
}

button.secondary {
  background: var(--bg-secondary);
  color: var(--text-secondary);
  box-shadow: none;
}

button.secondary:hover {
  background: var(--bg-tertiary);
  box-shadow: var(--shadow-md);
}

button:disabled {
  background: var(--bg-tertiary);
  color: var(--text-tertiary);
  cursor: not-allowed;
  box-shadow: none;
  opacity: 0.6;
}

/* Business type toggle */
.business-type-toggle {
  display: flex;
  gap: var(--space-sm);
  margin-bottom: var(--space-md);
  padding: var(--space-xs);
  background: var(--bg-secondary);
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-light);
}

.business-type-toggle button {
  flex: 1;
  padding: var(--space-sm) var(--space-md);
  font-size: var(--font-size-sm);
  background: transparent;
  color: var(--text-secondary);
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-base);
  font-weight: var(--font-medium);
  box-shadow: none;
}

.business-type-toggle button.active {
  background: var(--bg-primary);
  color: var(--primary);
  box-shadow: var(--shadow-sm);
  font-weight: var(--font-semibold);
}

.business-type-toggle button:hover:not(.active) {
  background: var(--bg-hover);
  color: var(--text-primary);
}

/* Company search results */
.search-results {
  max-height: 280px;
  transition: all var(--transition-base);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  margin-top: var(--space-xs);
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--bg-primary);
  z-index: 1000;
  overflow-y: auto;
  box-shadow: var(--shadow-lg);
  backdrop-filter: blur(10px);
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.search-result-item {
  padding: var(--space-md);
  transition: all var(--transition-base);
  border-bottom: 1px solid var(--border-light);
  cursor: pointer;
}

.search-result-item:hover {
  transform: translateX(4px);
  background-color: var(--bg-hover);
}

.company-name {
  font-size: var(--font-size-base);
  font-weight: var(--font-semibold);
  margin-bottom: var(--space-xs);
  transition: all var(--transition-base);
  letter-spacing: var(--tracking-tight);
  color: var(--text-primary);
}

.company-details {
  font-size: var(--font-size-sm);
  transition: all var(--transition-base);
  letter-spacing: var(--tracking-normal);
  color: var(--text-secondary);
}

.company-address {
  margin-bottom: var(--space-xs);
  line-height: var(--leading-relaxed);
}

.company-number {
  color: var(--text-tertiary);
  margin-bottom: var(--space-xs);
  font-size: var(--font-size-xs);
  font-weight: var(--font-medium);
}

.company-status {
  display: inline-block;
  padding: var(--space-xs) var(--space-sm);
  border-radius: var(--radius-sm);
  font-size: var(--font-size-xs);
  font-weight: var(--font-medium);
  margin-top: var(--space-xs);
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wide);
}

/* Search loading and error states */
.search-loading {
  position: absolute;
  right: var(--space-md);
  top: 50%;
  transform: translateY(-50%);
  font-size: var(--font-size-sm);
  color: var(--text-tertiary);
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

.search-loading::after {
  content: '';
  width: 1rem;
  height: 1rem;
  border: 2px solid var(--border-light);
  border-top-color: var(--primary);
  border-radius: var(--radius-full);
  animation: spin 0.8s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.search-error {
  font-size: var(--font-size-sm);
  color: var(--error);
  margin-top: var(--space-sm);
  padding: var(--space-sm);
  background-color: rgba(239, 68, 68, 0.1);
  border-radius: var(--radius-md);
  border: 1px solid rgba(239, 68, 68, 0.2);
}

/* Error message */
.error-message {
  font-size: var(--font-size-sm);
  padding: var(--space-sm) var(--space-md);
  margin-top: var(--space-sm);
  background: rgba(239, 68, 68, 0.1);
  color: var(--error);
  border-radius: var(--radius-md);
  white-space: pre-line;
  animation: shake 0.3s ease-in-out;
  box-shadow: var(--shadow-sm);
  border: 1px solid rgba(239, 68, 68, 0.2);
  font-weight: var(--font-medium);
  text-align: center;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-4px); }
  75% { transform: translateX(4px); }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .timeline {
    max-width: 100%;
  }
  
  .timeline-container {
    padding: var(--space-xs);
  }

  .timeline::before {
    left: 8px;
  }

  .timeline-step {
    padding-left: 28px;
  }

  .timeline-marker {
    left: 0px;
  }

  .timeline-content {
    padding: var(--space-sm) var(--space-md);
  }
  
  .timeline-content h2 {
    font-size: var(--font-size-base);
  }
  
  .step-description {
    font-size: var(--font-size-xs);
  }
  
  input, button {
    font-size: var(--font-size-sm);
  }
  
  input {
    height: 40px;
  }
  
  button {
    padding: var(--space-xs) var(--space-md);
  }
}

/* Animation keyframes */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Apply animations to timeline steps */
.timeline-step {
  animation: fadeIn 0.4s ease forwards;
  animation-delay: calc(var(--step-index, 0) * 0.1s);
}

.timeline-content {
  animation: slideIn 0.4s ease forwards;
  animation-delay: calc(var(--step-index, 0) * 0.1s);
}

/* Disabled state styles */
.timeline-step.upcoming:not(.has-url-params) input,
.timeline-step.upcoming:not(.has-url-params) button {
  opacity: 0.6;
  pointer-events: none;
  background: var(--bg-tertiary);
}

.timeline-step.completed input {
  opacity: 1;
  background-color: var(--bg-primary);
}

/* Pre-filled field indicators */
.timeline-step.has-url-params.completed .timeline-content {
  background: rgba(70, 200, 187, 0.02);
  border-left: 3px solid var(--primary);
}

.timeline-step.has-url-params.completed .timeline-content::before {
  content: '✓ Securely pre-filled from Commercial Experts';
  display: block;
  font-size: var(--font-size-xs);
  color: var(--primary);
  font-weight: var(--font-medium);
  margin-bottom: var(--space-xs);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wide);
}

.tfli-header {
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-light);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  box-shadow: var(--shadow-sm);
}

.header-content {
  max-width: 800px;
  margin: 0 auto;
  padding: var(--space-md);
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

.logo-container {
  display: flex;
  align-items: center;
  gap: var(--space-lg);
  flex-wrap: wrap;
}

.logo {
  display: flex;
  align-items: center;
}

.logo img {
  height: 32px;
  width: auto;
  filter: brightness(0.95);
  transition: all var(--transition-base);
}

.logo img:hover {
  filter: brightness(1.05);
  transform: scale(1.02);
}

.logo a {
  display: inline-block;
  text-decoration: none;
}

.tfli-logo img {
  height: 32px;
}

.swoop-logo img {
  height: 32px;
}

.logo-separator {
  display: flex;
  align-items: center;
  margin: 0 var(--space-sm);
}

.partnership-text {
  font-size: var(--font-size-xs);
  color: var(--text-tertiary);
  font-weight: var(--font-medium);
  letter-spacing: var(--tracking-wide);
  white-space: nowrap;
}

/* Responsive adjustments for co-branding */
@media (max-width: 768px) {
  .logo-container {
    gap: var(--space-md);
    justify-content: center;
    width: 100%;
  }
  
  .partnership-text {
    font-size: var(--font-size-xs);
  }
  
  .tfli-logo img,
  .swoop-logo img {
    height: 24px;
  }
  
  .tfli-logo img {
    height: 22px;
  }
  
  /* #notadev😝 - Mobile toast notification adjustments */
  .toast-notification {
    bottom: var(--space-sm);
    max-width: calc(100% - var(--space-md));
    font-size: var(--font-size-xs);
    padding: var(--space-xs) var(--space-sm);
  }
  
  .toast-icon {
    font-size: var(--font-size-base);
  }
}

main {
  margin-top: 72px;
  animation: fadeIn 0.5s ease;
}

/* Trust indicator */
.trust-indicator {
  text-align: center;
  margin-top: var(--space-xl);
  padding: var(--space-md);
  animation: fadeIn 0.8s ease;
}

.trust-indicator p {
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-medium);
  margin: 0;
  letter-spacing: var(--tracking-normal);
}

.company-search {
  position: relative;
  margin-bottom: 1rem;
  width: 100%;
}

.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.password-input-wrapper input {
  padding-right: calc(var(--space-xl) * 2);
  margin-bottom: 0;
}

.toggle-password {
  position: absolute;
  right: var(--space-sm);
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--space-xs) var(--space-sm);
  font-size: var(--font-size-sm);
  color: var(--text-tertiary);
  transition: all var(--transition-base);
  border-radius: var(--radius-sm);
  height: auto;
  min-width: auto;
  box-shadow: none;
}

.toggle-password:hover {
  background: var(--bg-hover);
  color: var(--primary);
}

/* Name inputs */
.name-inputs {
  display: flex;
  gap: var(--space-md);
  width: 100%;
  box-sizing: border-box;
  flex-wrap: wrap;
}

.name-inputs input {
  flex: 1;
  min-width: 150px;
  margin-bottom: 0;
}

/* Error styles */
.timeline-step.has-error .timeline-marker {
  background: var(--error);
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.15);
}

.timeline-step.has-error .timeline-content {
  border-color: var(--error);
}

.timeline-step.completed.has-error .timeline-marker {
  background: var(--primary);
  box-shadow: 0 0 0 4px var(--primary-bg);
}

.timeline-step.completed.has-error .timeline-content {
  border-color: var(--primary);
}

input.error {
  border-color: var(--error);
  background-color: rgba(239, 68, 68, 0.05);
  animation: shake 0.3s ease-in-out;
}

input.error:focus {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.15), var(--shadow-sm);
}

/* Button loading state */
.button-loading {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  justify-content: center;
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

button:disabled .spinner {
  border-color: rgba(0, 0, 0, 0.1);
  border-top-color: var(--text-tertiary);
}
</style> 